/* eslint-disable @typescript-eslint/no-use-before-define */
import parse, {
  attributesToProps,
  domToReact,
  Element,
  HTMLReactParserOptions,
  htmlToDOM,
  Text,
} from 'html-react-parser';
import { Helmet } from 'react-helmet';

import Button from '@/components/elements/Button/BaseButton';
import Image from '@/components/elements/Image';

import { parseCmsSrcset } from '../utils';
import {
  Anchor,
  Blockquote,
  ButtonWrapper,
  H2,
  H3,
  IframeWrapper,
  ImageWrapper,
  MediaWrapper,
  StyledLink,
  StyledTable,
  TableWrapper,
  Ul,
} from './style';

const parseOption: HTMLReactParserOptions = {
  replace: domNode => {
    const { name, attribs, children } = domNode as Element;
    const attrProps = attributesToProps(attribs);

    switch (name) {
      case 'h1':
        return (
          <H2 id={parseDOMText(children as Element[], '') || undefined}>
            {parseChildren(children)}
          </H2>
        );
      case 'h2':
        return (
          <H3 id={parseDOMText(children as Element[], '') || undefined}>
            {parseChildren(children)}
          </H3>
        );
      case 'a': {
        // children の内容からテキスト全体を取得
        const linkText = parseDOMText(children as Element[], '');

        // テキスト先頭に[button]が入っている場合はボタンのような見た目になるようにする
        if (linkText.startsWith('[button]')) {
          return (
            <ButtonWrapper>
              <a href={attribs.href}>
                <Button
                  bgColor="primary"
                  size="m"
                  radius="l"
                  width="100%"
                  height="100%"
                  fontWeight="bold"
                >
                  {linkText.replace('[button]', '')}
                </Button>
              </a>
            </ButtonWrapper>
          );
        }
        // テキスト先頭に[entry]が入っている場合はボタンのような見た目になるようにする
        if (linkText.startsWith('[entry]')) {
          return (
            <ButtonWrapper>
              <Button
                bgColor="primary"
                size="m"
                radius="l"
                width="100%"
                height="100%"
                fontWeight="bold"
                id="entry-button"
              >
                エントリーする
              </Button>
            </ButtonWrapper>
          );
        }
        if (attribs.href.startsWith(import.meta.env.BASE_URL)) {
          return (
            <StyledLink
              to={attribs.href}
              style={parseStyleString(attribs.style)}
            >
              {parseChildren(children)}
            </StyledLink>
          );
        } else if (attribs.href.startsWith('#')) {
          return (
            <Anchor
              href={attribs.href}
              rel="noreferrer"
              style={parseStyleString(attribs.style)}
            >
              {parseChildren(children)}
            </Anchor>
          );
        }
        return (
          <Anchor
            href={attribs.href}
            target="_blank"
            rel="noreferrer"
            style={parseStyleString(attribs.style)}
          >
            {parseChildren(children)}
          </Anchor>
        );
      }
      case 'img': {
        return (
          <ImageWrapper>
            <Image
              srcSet={parseCmsSrcset({
                imageUrl: attribs.src,
              })}
              mediaSet={[
                {
                  srcSet: parseCmsSrcset({
                    imageUrl: attribs.src,
                    format: 'webp',
                  }),
                  type: 'image/webp',
                },
              ]}
              alt={attribs.alt}
              width={['100%', '100%', '480px', '480px']}
              aspectRatio={Number(attribs.height) / Number(attribs.width)}
            />
          </ImageWrapper>
        );
      }
      case 'iframe':
        return (
          <IframeWrapper>
            <iframe src={attribs.src} title={attribs.title} loading="lazy" />
          </IframeWrapper>
        );
      case 'ul':
        return <Ul>{parseChildren(children)}</Ul>;
      case 'blockquote': {
        return (
          <MediaWrapper>
            <Blockquote {...attrProps}>{parseChildren(children)}</Blockquote>
          </MediaWrapper>
        );
      }
      case 'script': {
        return (
          <Helmet>
            <script {...attrProps} />
          </Helmet>
        );
      }
      case 'table': {
        return (
          <TableWrapper>
            <StyledTable {...attrProps}>{parseChildren(children)}</StyledTable>
          </TableWrapper>
        );
      }
      default:
        return null;
    }
  },
};

const parseChildren = (children: Element['children']) => {
  return domToReact(children, parseOption);
};

export const htmlToThemed = (html: string): ReturnType<typeof parse> => {
  return parse(html, parseOption);
};

const parseDOMText = (
  dom: ReturnType<typeof htmlToDOM>,
  separator: string = ' ',
): string => {
  return dom
    .reduce((acc, current) => {
      if (['script', 'iframe'].includes((current as Element).name)) {
        return acc;
      }

      let newText = '';
      if ((current as Element).children) {
        newText = parseDOMText(
          (current as Element).children as ReturnType<typeof htmlToDOM>,
          separator,
        );
      } else if (current.type === 'text') {
        newText = (current as Text).data;
      }

      return newText === '' ? acc : [...acc, newText];
    }, [] as string[])
    .join(separator);
};

const parseStyleString = (styleStr?: string | null) => {
  if (!styleStr) {
    return {};
  }
  return styleStr.split(';').reduce((acc, style) => {
    const colonPosition = style.indexOf(':');

    if (colonPosition === -1) {
      return acc;
    }

    const camelCaseProperty = style
      .substring(0, colonPosition)
      .trim()
      .replace(/^-ms-/, 'ms-')
      .replace(/-./g, c => c.substring(1).toUpperCase());
    const value = style.substring(colonPosition + 1).trim();

    return value ? { ...acc, [camelCaseProperty]: value } : acc;
  }, {});
};

export const htmlToTextContent = (html: string): string => {
  return parseDOMText(htmlToDOM(html));
};
